import { List, Map } from "immutable";
import { Observable, observable } from "micro-observables";
import { Signal } from "micro-signals";
import { Page, PaginationOptions } from "@utils/page";
import { PaginatedData } from "@core/paginatedData";

export interface MappedStore<T, S> {
	fetch: (id: S) => Promise<void>;
	fetchMore: (id: S) => Promise<void>;
	onChange: Signal<{ id: S; data: PaginatedData<T> | null }>;
	getObservable: (id: S) => Observable<PaginatedData<T> | null>;
	updateContent(id: S, updater: (content: List<T>) => List<T>): void;
}

export class SimpleMappedStore<T, S extends string = string> implements MappedStore<T, S> {
	_mappedItems = observable(Map<S, PaginatedData<T>>());

	onChange = new Signal<{ id: S; data: PaginatedData<T> | null }>();

	constructor(private readonly _fetchList: (id: S, page: PaginationOptions) => Promise<Page<T>>) {}

	updateContent(id: S, updater: (content: List<T>) => List<T>): void {
		this._mappedItems.update((items) => {
			const data = items.get(id, null);
			if (!data) {
				return items;
			}
			return items.set(id, { ...data, content: updater(data.content) });
		});
	}

	update(id: S, updater: (data: PaginatedData<T>) => PaginatedData<T>): void {
		this._mappedItems.update((items) => {
			const data = items.get(id, null);
			if (!data) {
				return items;
			}
			return items.set(id, updater(data));
		});
	}

	async fetch(id: S): Promise<void> {
		const result = await this._fetchList(id, { page: 0 });
		this._mappedItems.update((items) => items.set(id, { ...result, content: List(result.content) }));
		this.dispatchChange(id);
	}

	async fetchMore(id: S): Promise<void> {
		const currentItems = this._mappedItems.get().get(id);
		{
			if (!currentItems) {
				return this.fetch(id);
			}
		}
		const newItems = await this._fetchList(id, { page: currentItems.page + 1 });
		this._mappedItems.update((items) =>
			items.set(id, {
				...newItems,
				content: currentItems.content.concat(newItems.content),
			})
		);
		this.dispatchChange(id);
	}

	getObservable(id: S): Observable<PaginatedData<T> | null> {
		return this._mappedItems.transform((items) => items.get(id, null));
	}

	private dispatchChange(id: S): void {
		this.onChange.dispatch({ id, data: this._mappedItems.get().get(id, null) });
	}
}
